import { useEffect, useState } from "react";
import type { TypedWrappedFieldProps } from "redux-form";
import cs from "classnames";

import { Box, RoundedBox } from "@braintrust/braintrust-ui-components";
import { useUser } from "@js/apps/common/hooks";
import { AttachmentRenameInput, renameAttachment } from "@js/apps/file-upload";
import { StoreResumeCheckboxField } from "@js/apps/jobs/forms/fields/resume/store-resume";
import { useAppSelector } from "@js/hooks";
import { SessionStorage } from "@js/services";
import type { Attachment } from "@js/types/uploads";
import { deepClone } from "@js/utils";

import { ResumeUpload } from "./resume-upload";

import style from "./style.module.scss";

const uploadType = ENUMS.UploadType.RESUME_ATTACHMENT_UPLOAD_TYPE;

export type ResumeFieldProps = TypedWrappedFieldProps<{
  id: number;
  attachment: Attachment;
}> & {
  hideCheckbox?: boolean;
  isProfileResumeAdded?: boolean;
  uploaderClassName?: string;
};

export const AddResumeField = ({
  uploaderClassName,
  ...resumeField
}: ResumeFieldProps) => {
  const { hasExistingOrUploadedResume, wasResumeAlreadyUploaded } =
    useAddResumeField(resumeField);

  return (
    <Box className={style.resume}>
      {hasExistingOrUploadedResume ? (
        <ResumeAdded
          resumeField={resumeField}
          wasResumeOnceUploaded={wasResumeAlreadyUploaded}
        />
      ) : (
        <Box className={uploaderClassName}>
          <ResumeUpload
            {...resumeField}
            onUpload={(arg) => {
              resumeField.input.onChange(arg);
            }}
          />
        </Box>
      )}
    </Box>
  );
};

const useAddResumeField = ({
  isProfileResumeAdded,
  input,
}: ResumeFieldProps) => {
  const [wasResumeAlreadyUploaded, setWasResumeAlreadyUploaded] =
    useState(false);

  const hasExistingOrUploadedResume = !!input.value;

  useEffect(() => {
    const wasResumeOnceUploaded =
      SessionStorage.getItem(SessionStorage.keys.TALENT_RESUME_UPLOADED) ||
      undefined;

    if (isProfileResumeAdded || wasResumeOnceUploaded) {
      SessionStorage.setItem(
        SessionStorage.keys.TALENT_RESUME_UPLOADED,
        "true",
      );
      setWasResumeAlreadyUploaded(true);
    }

    if (!isProfileResumeAdded && hasExistingOrUploadedResume) {
      SessionStorage.setItem(
        SessionStorage.keys.TALENT_RESUME_UPLOADED,
        "true",
      );

      setWasResumeAlreadyUploaded((prev) => {
        if (!prev) return prev;
        return true;
      });
    }
  }, [hasExistingOrUploadedResume, isProfileResumeAdded]);

  return {
    wasResumeAlreadyUploaded,
    hasExistingOrUploadedResume,
  };
};

type ResumeAddedProps = {
  resumeField: ResumeFieldProps;
  wasResumeOnceUploaded: boolean;
};

const ResumeAdded = ({
  resumeField,
  wasResumeOnceUploaded,
}: ResumeAddedProps) => {
  const user = useUser();
  const resume = resumeField.input.value;

  const isRenameDisabled = !user;
  const profileResumeId = useAppSelector(
    (state) => state.freelancer.freelancerProfile?.resume?.id,
  );
  if (!resumeField.input.value) return null;

  const isResumeFromProfile = profileResumeId === resumeField.input.value?.id;

  const handleRenameResume = async (name: string) => {
    const hasNameChanged = name !== resume.attachment.name;
    if (!hasNameChanged) {
      return;
    }

    if (!!name) {
      const newResume = deepClone(resume);
      newResume.attachment.name = name;
      resumeField.input.onChange(newResume);
    }

    return renameAttachment({
      uuid: resume.attachment.id,
      uploadType,
      name,
    });
  };

  const handleRemoveResume = () => {
    resumeField.input.onChange(null);
  };

  const isStoreResumeAvailable =
    !resumeField.hideCheckbox && !isResumeFromProfile;

  return (
    <RoundedBox
      className={cs(style.resumeAdded, {
        [style.resumeAddedWithStoreResume]: isStoreResumeAvailable,
      })}
    >
      <Box className={style.resumeAddedHeader}>
        <AttachmentRenameInput
          id="resumeName"
          value={resume.attachment.name}
          loading={false}
          onRename={!isRenameDisabled ? handleRenameResume : undefined}
          onDelete={handleRemoveResume}
        />
      </Box>
      {isStoreResumeAvailable && (
        <Box sx={{ ml: 2.5 }}>
          <StoreResumeCheckboxField
            wasResumeOnceUploaded={wasResumeOnceUploaded}
          />
        </Box>
      )}
    </RoundedBox>
  );
};
